import React, { Component, Fragment } from 'react';
import { connect, Provider } from 'react-redux';

import Card, { CardSection } from '@lightspeed/cirrus-card';
import Text from '@lightspeed/cirrus-text';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { IconCategories } from '@lightspeed/flame/Icon/Categories';
import { IconDelete } from '@lightspeed/flame/Icon/Delete';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { IconImages } from '@lightspeed/flame/Icon/Images';
import { IconInventory } from '@lightspeed/flame/Icon/Inventory';
import { IconSync } from '@lightspeed/flame/Icon/Sync';
import Layout, { LayoutSection } from 'cirrus/Layout';
import get from 'lodash/get';

import { createReactRenderer } from 'helpers/react-renderer';
import { decodeHtml } from 'helpers/utils';
import { addClassName } from 'pages/gui-defs/utils/className';
import { clearData, getData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import store from 'pages/store/index';
import { entities } from 'pages/store/schema';
import { getErrors, getProcessing, getValues } from 'pages/store/selectors';
import Loading from 'pages/views/generic/Loading';

import DeleteEcomModal from './components/DeleteEcomModal';
import EnableAccountModal from './components/EnableAccountModal';
import EnableAccountSwitch from './components/EnableAccountSwitch';
import LanguageModal from './components/LanguageModal';
import PublishLimitModal from './components/PublishLimitModal';
import ShopModal from './components/ShopModal';
import SyncCategoriesModal from './components/SyncCategoriesModal';
import SyncImagesModal from './components/SyncImagesModal';
import SyncProductsModal from './components/SyncProductsModal';
import SyncStockLevelsModal from './components/SyncStockLevelsModal';

const STATUS_QUEUED = 'queued';
const STATUS_CREATED = 'created';
const STATUS_COMPLETED = 'completed';
const STATUS_FAILED = 'failed';
const STATUS_EXPIRED = 'expired';
const STATUS_CLOSED = 'closed';

const mapStatusToMessage = {
  [STATUS_QUEUED]: 'Preparing to install',
  [STATUS_CREATED]: 'Installation in progress',
  [STATUS_COMPLETED]: 'Installation complete',
  [STATUS_FAILED]: 'Installation failed',
  [STATUS_EXPIRED]: 'Installation expired',
  [STATUS_CLOSED]: 'eCom subscription closed',
  undefined: 'eCom is not installed',
};

export const getStatusMessage = (status) => {
  if (status !== '') {
    return mapStatusToMessage[status] || status;
  }
  return 'N/A';
};

class EcomRadManagement extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isInitialLoad: true,
      openedModal: '',
    };

    this.documentationUrls = {
      syncProducts: 'https://lightspeedhq.atlassian.net/wiki/x/6oBPC#expand-Whatitdoes',
      syncCategories:
        'https://lightspeedhq.atlassian.net/wiki/x/6oBPC#RADSupportTool-Nuke&ReseteComCategories',
      syncImages: 'https://lightspeedhq.atlassian.net/wiki/x/6oBPC#RADSupportTool-ResynceComImages',
    };

    this.audioRef = React.createRef();

    this.toggleModal = this.toggleModal.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.getEcomIntegrationData = this.getEcomIntegrationData.bind(this);
  }

  componentDidMount() {
    addClassName({ selector: '.view_tab_ecom', className: 'cirrus-tab' });
    this.getEcomIntegrationData().then(() => {
      this.setState({
        isInitialLoad: false,
      });
    });
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  getEcomIntegrationData() {
    return this.props.getEcomIntegrationData(`/staff/accounts/${this.props.accountId}/ecom`);
  }

  toggleModal(modal) {
    this.setState({
      openedModal: modal || '',
    });
  }

  handleConfirm({ updateData, reload, forceClearData, message } = {}) {
    window.flashNotifier('success', message || 'Ecom Integration updated.', 3);

    if (forceClearData) {
      this.props.clearData();
    }

    if (updateData) {
      this.getEcomIntegrationData();
    }

    if (reload) {
      window.location.reload();
    }
  }

  render() {
    const hasEcomIntegration = !get(this.props, 'loadingErrors');
    const {
      isLoading,
      accountId,
      canSync,
      canDelete,
      isStockLevelSyncJobRunning,
      integrationId,
      ecom: {
        url,
        enabled = false,
        status,
        status_text: statusText,
        publish_limit: publishLimit,
        shop_id: shopId,
        shop_name: shopName,
        language_code: languageCode,
        language_name: languageName,
      } = {},
    } = this.props;
    const { isInitialLoad, openedModal, isPlaying } = this.state;

    return (
      <div className="cirrus cr-position-relative" style={{ minHeight: '450px' }}>
        {isInitialLoad ? (
          <Loading className="cr-bg-snow-200" />
        ) : (
          <Fragment>
            <Layout>
              <LayoutSection
                title="eCom Account"
                description="Information on this eCom Store."
                data-test="ecom-info-section"
              >
                <Card>
                  {hasEcomIntegration &&
                    (status === STATUS_COMPLETED ||
                      status === STATUS_EXPIRED ||
                      status === STATUS_CLOSED) && (
                      <Fragment>
                        {status === STATUS_COMPLETED && (
                          <Fragment>
                            <CardSection noSpacing>
                              <div className="cr-text-left cr-flex cr-text cr-justify-between cr-items-center cr-p-3">
                                <a
                                  href={`https://${url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-test="shop-link"
                                >
                                  {url}
                                </a>

                                <a
                                  href={`https://${url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconExternalLink color="blue" />
                                </a>
                              </div>
                            </CardSection>
                            <Divider />
                          </Fragment>
                        )}
                        <CardSection noSpacing>
                          <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                            <Text body color="gray">
                              Enabled
                            </Text>
                            <EnableAccountSwitch
                              data-test="enabled-switch"
                              disabled={enabled}
                              checked={enabled}
                              onChange={() => this.toggleModal('enableAccount')}
                            />
                          </div>
                          {openedModal === 'enableAccount' && (
                            <EnableAccountModal
                              accountId={accountId}
                              integrationId={integrationId}
                              onClose={this.toggleModal}
                              onConfirm={this.handleConfirm}
                            />
                          )}
                        </CardSection>
                        <Divider />
                      </Fragment>
                    )}
                  <CardSection noSpacing>
                    <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                      <Text body color="gray">
                        Status
                      </Text>
                      <Text body bold color="gray" data-test="status-text">
                        {getStatusMessage(status)}
                      </Text>
                    </div>
                  </CardSection>
                  {status !== STATUS_COMPLETED && status !== STATUS_CLOSED && statusText && (
                    <CardSection noSpacing className="cr-ph-3 cr-pb-3">
                      <Text bodySmall dimmed>
                        {decodeHtml(statusText)}
                      </Text>
                    </CardSection>
                  )}
                </Card>
              </LayoutSection>
              {hasEcomIntegration && (
                <Fragment>
                  {status === STATUS_COMPLETED && (
                    <Fragment>
                      <LayoutSection
                        title="eCom Settings"
                        description="Configuration options for this eCom Store."
                        data-test="ecom-settings-section"
                      >
                        <Card>
                          <CardSection noSpacing>
                            <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                              <Text body bold>
                                Publish to eCom Item Limit
                              </Text>
                              <Text body color="gray" data-test="publish-limit-text">
                                {publishLimit}
                              </Text>
                              {canSync && (
                                <Text
                                  body
                                  bold
                                  color="blue"
                                  className="cr-cursor-pointer"
                                  onClick={() => this.toggleModal('publishLimit')}
                                  data-test="publish-limit-button"
                                >
                                  Edit
                                </Text>
                              )}
                            </div>
                            {openedModal === 'publishLimit' && (
                              <PublishLimitModal
                                accountId={accountId}
                                publishLimit={publishLimit}
                                onClose={this.toggleModal}
                                onConfirm={this.handleConfirm}
                              />
                            )}
                          </CardSection>
                          <Divider />
                          <CardSection noSpacing>
                            <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                              <Text body bold>
                                eCom Default Language
                              </Text>
                              <Text body color="gray" className="cr-ph-2" data-test="language-text">
                                {languageName}
                              </Text>
                              {canSync && (
                                <Text
                                  body
                                  bold
                                  color="blue"
                                  className="cr-cursor-pointer"
                                  onClick={() => this.toggleModal('language')}
                                  data-test="language-button"
                                >
                                  Edit
                                </Text>
                              )}
                            </div>
                            {openedModal === 'language' && (
                              <LanguageModal
                                accountId={accountId}
                                languageCode={languageCode}
                                onClose={this.toggleModal}
                                onConfirm={this.handleConfirm}
                              />
                            )}
                          </CardSection>
                          <Divider />
                          <CardSection noSpacing>
                            <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                              <Text body bold>
                                Synchronize from Shop
                              </Text>
                              <Text body color="gray" className="cr-ph-2" data-test="shop-text">
                                {decodeHtml(shopName)}
                              </Text>
                              {canSync && (
                                <Text
                                  body
                                  bold
                                  color="blue"
                                  className="cr-cursor-pointer"
                                  onClick={() => this.toggleModal('shop')}
                                  data-test="shop-button"
                                >
                                  Edit
                                </Text>
                              )}
                            </div>
                            {openedModal === 'shop' && (
                              <ShopModal
                                accountId={accountId}
                                shopId={shopId}
                                onClose={this.toggleModal}
                                onConfirm={this.handleConfirm}
                              />
                            )}
                          </CardSection>
                        </Card>
                      </LayoutSection>
                      <Divider />
                      {canSync && (
                        <Fragment>
                          <LayoutSection
                            title="eCom Actions"
                            description="Extra powerful eCom actions, for the distinguished Support power-user."
                            data-test="ecom-sync-section"
                          >
                            <Card>
                              <CardSection noSpacing>
                                <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                                  <Text style={{ maxWidth: '50%' }}>
                                    Will sync missing or inaccurate item data from Retail to eCom.
                                    Will not override changes made in eCom. Includes: Description,
                                    pricing, IDs, category, brand, images.{' '}
                                    <a
                                      href={this.documentationUrls.syncProducts}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      See documentation for details
                                    </a>
                                  </Text>
                                  <Button
                                    variant="secondary"
                                    fill
                                    onClick={() => this.toggleModal('syncProducts')}
                                    className="cr-ml-2"
                                    data-test="sync-products-button"
                                  >
                                    <IconSync />
                                    <span className="cr-white-space-no-wrap">
                                      Sync eCom Item Data
                                    </span>
                                  </Button>
                                </div>
                                {openedModal === 'syncProducts' && (
                                  <SyncProductsModal
                                    accountId={accountId}
                                    onClose={this.toggleModal}
                                    onConfirm={this.handleConfirm}
                                  />
                                )}
                              </CardSection>
                              <Divider />
                              <CardSection noSpacing>
                                <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                                  <Text style={{ maxWidth: '50%' }}>
                                    Erases <strong>ALL</strong> Categories from eCom and replaces
                                    them with the Categories from Retail.{' '}
                                    <a
                                      href={this.documentationUrls.syncCategories}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      See documentation for details
                                    </a>
                                  </Text>
                                  <Button
                                    variant="secondary"
                                    fill
                                    onClick={() => this.toggleModal('syncCategories')}
                                    className="cr-ml-2"
                                    data-test="sync-categories-button"
                                  >
                                    <IconCategories />
                                    <span className="cr-white-space-no-wrap">
                                      Erase &amp; reset eCom Categories
                                    </span>
                                  </Button>
                                </div>
                                {openedModal === 'syncCategories' && (
                                  <SyncCategoriesModal
                                    accountId={accountId}
                                    onClose={this.toggleModal}
                                    onConfirm={this.handleConfirm}
                                  />
                                )}
                              </CardSection>
                              <Divider />
                              <CardSection noSpacing>
                                <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                                  <Text style={{ maxWidth: '50%' }}>
                                    Sync all product images from Retail to eCom and uses Retail as a
                                    source of truth for all eCom images, moving forward. *Doesn’t
                                    delete images from eCom and doesn’t override eCom image limit.{' '}
                                    <a
                                      href={this.documentationUrls.syncImages}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      See documentation for details
                                    </a>
                                  </Text>
                                  <Button
                                    variant="secondary"
                                    fill
                                    onClick={() => this.toggleModal('syncImages')}
                                    className="cr-ml-2"
                                    data-test="sync-images-button"
                                  >
                                    <IconImages />
                                    <span className="cr-white-space-no-wrap">Sync eCom Images</span>
                                  </Button>
                                </div>
                                {openedModal === 'syncImages' && (
                                  <SyncImagesModal
                                    accountId={accountId}
                                    onClose={this.toggleModal}
                                    onConfirm={this.handleConfirm}
                                  />
                                )}
                              </CardSection>
                              <Divider />
                              <CardSection noSpacing>
                                <div className="cr-text-left cr-flex cr-justify-between cr-items-center cr-p-3">
                                  <Text style={{ maxWidth: '50%' }}>
                                    Sync all product stock levels from Retail to eCom and uses
                                    Retail as the source of truth.
                                  </Text>
                                  <Button
                                    variant="secondary"
                                    fill
                                    onClick={() => this.toggleModal('syncStockLevels')}
                                    className="cr-ml-2"
                                    data-test="sync-stock-levels-button"
                                    disabled={isStockLevelSyncJobRunning}
                                  >
                                    <IconInventory />
                                    <span className="cr-white-space-no-wrap">
                                      {isStockLevelSyncJobRunning
                                        ? 'eCom stock levels sync in progress...'
                                        : 'Sync eCom stock levels'}
                                    </span>
                                  </Button>
                                </div>
                                {openedModal === 'syncStockLevels' && (
                                  <SyncStockLevelsModal
                                    accountId={accountId}
                                    onClose={this.toggleModal}
                                    onConfirm={this.handleConfirm}
                                  />
                                )}
                              </CardSection>
                            </Card>
                          </LayoutSection>
                          <Divider />
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                  {canDelete && (
                    <LayoutSection
                      title="Danger Zone"
                      description="The only place to permanently delete an eCom integration."
                      data-test="ecom-delete-section"
                    >
                      <Card>
                        <CardSection noSpacing className="cr-position-relative">
                          <div className="cr-p-3">
                            <Text>
                              Deletes the eCom integration <strong>PERMANENTLY</strong> for this
                              account.
                            </Text>
                            <Text>
                              This action is <strong>destructive</strong> and{' '}
                              <strong>cannot be undone</strong>.
                            </Text>
                            <div className="cr-text-center cr-pt-3">
                              <Button
                                variant="danger"
                                fill
                                onClick={() => this.toggleModal('deleteEcom')}
                                onMouseEnter={() => {
                                  this.audioRef.current.play();
                                  this.setState({ isPlaying: true });
                                }}
                                onMouseLeave={() => {
                                  setTimeout(() => {
                                    if (openedModal !== 'deleteEcom') {
                                      this.audioRef.current.pause();
                                      this.setState({ isPlaying: false });
                                    }
                                  }, 100);
                                }}
                                style={{ position: 'relative', zIndex: 2 }}
                                data-test="delete-ecom-button"
                              >
                                <IconDelete />
                                <span className="cr-white-space-no-wrap">
                                  Delete eCom Integration
                                </span>
                              </Button>
                            </div>
                            {openedModal === 'deleteEcom' && (
                              <DeleteEcomModal
                                accountId={accountId}
                                onConfirm={this.handleConfirm}
                                onAfterOpen={() => {
                                  this.audioRef.current.play();
                                }}
                                onClose={() => {
                                  this.toggleModal();
                                  this.audioRef.current.pause();
                                  this.audioRef.current.currentTime = 0;
                                  this.setState({ isPlaying: false });
                                }}
                              />
                            )}
                          </div>
                          {isPlaying ? (
                            <img
                              src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/ecom/dz.gif"
                              alt=""
                              className="cr-position-absolute"
                              style={{ bottom: 0, left: '10%', width: '80%', height: '150%' }}
                            />
                          ) : null}
                          <audio preload="auto" ref={this.audioRef}>
                            <source src="/images/ecom/dz.mp3" type="audio/mpeg" />
                          </audio>
                        </CardSection>
                      </Card>
                    </LayoutSection>
                  )}
                </Fragment>
              )}
            </Layout>
            {isLoading && <Loading />}
          </Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  ecom: getValues(entities.ecomIntegration)(state).ecom,
  isLoading: !!getProcessing({
    entity: entities.ecomIntegration,
    list: false,
    actionType: actionTypes.LOADING,
  })(state),
  loadingErrors: getErrors({
    entity: entities.ecomIntegration,
    list: false,
    actionType: actionTypes.LOADING,
  })(state),
});

const mapDispatchToProps = (dispatch) => ({
  getEcomIntegrationData: (url) =>
    dispatch(getData({ url, entity: entities.ecomIntegration, list: false })),
  clearData: () => dispatch(clearData(entities.ecomIntegration)),
});

const ConnectedEcomRadManagement = connect(mapStateToProps, mapDispatchToProps)(EcomRadManagement);

const StoreWrappedEcomRadManagement = (props) => (
  <Provider store={store}>
    <ConnectedEcomRadManagement {...props} />
  </Provider>
);

const RenderedComponent = createReactRenderer({
  component: StoreWrappedEcomRadManagement,
  selector: '[data-react-root="EcomRadManagement"]',
});

history.onChangeState(
  { name: 'system.views.account', tab: 'ecom', id: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default ConnectedEcomRadManagement;
